// Quebec statutory holidays, computed by rule (owner decision): the default email holidays when
// configuration.config.gavion.holidays is not set. SQL twin: supabase/schemas/45_gavion_quebec_holidays.sql
// (private.easter_sunday, private.quebec_holidays, private.quebec_holidays_around); keep them identical.
// A holiday on a weekend is not moved to a weekday: email send windows already skip non-business days.
import { BUSINESS_TZ, localDateOf } from "../gavion/time/torontoTime.ts";

const pad = (n: number): string => String(n).padStart(2, "0");
const ymd = (y: number, m: number, d: number): string =>
  `${String(y).padStart(4, "0")}-${pad(m)}-${pad(d)}`;
const DAY_MS = 86_400_000;
const utc = (y: number, m: number, d: number) =>
  new Date(Date.UTC(y, m - 1, d));
const ymdOf = (t: Date): string => t.toISOString().slice(0, 10);
/** ISO weekday (1 = Monday … 7 = Sunday). */
const isoDow = (t: Date): number => ((t.getUTCDay() + 6) % 7) + 1;
const plusDays = (t: Date, n: number): Date =>
  new Date(t.getTime() + n * DAY_MS);
/** The first Monday on or after y-m-d. */
const mondayOnOrAfter = (y: number, m: number, d: number): Date => {
  const t = utc(y, m, d);
  return plusDays(t, (8 - isoDow(t)) % 7);
};

/** Gregorian Easter Sunday (anonymous Gregorian computus: Meeus/Jones/Butcher). */
export function easterSunday(year: number): string {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return ymd(year, month, day);
}

/** The eight Quebec statutory holidays of `year`, YYYY-MM-DD, in date order. */
export function quebecHolidays(year: number): string[] {
  const [, em, ed] = easterSunday(year).split("-").map(Number);
  const may24 = utc(year, 5, 24);
  return [
    ymd(year, 1, 1), // Jour de l'An
    ymdOf(plusDays(utc(year, em, ed), -2)), // Vendredi saint
    ymdOf(plusDays(may24, -(isoDow(may24) - 1))), // Journée nationale des patriotes: Monday before May 25
    ymd(year, 6, 24), // Fête nationale
    ymd(year, 7, 1), // Fête du Canada
    ymdOf(mondayOnOrAfter(year, 9, 1)), // Fête du Travail: 1st Monday of September
    ymdOf(plusDays(mondayOnOrAfter(year, 10, 1), 7)), // Action de grâce: 2nd Monday of October
    ymd(year, 12, 25), // Noël
  ];
}

export const QUEBEC_HOLIDAY_YEARS_BEFORE = 1;
export const QUEBEC_HOLIDAY_YEARS_AFTER = 10;

/** Quebec holidays of the America/Toronto year of `now`, one year back and ten years ahead. */
export function quebecHolidaysAround(now: Date): string[] {
  const year = Number(localDateOf(now, BUSINESS_TZ).slice(0, 4));
  const out: string[] = [];
  for (
    let y = year - QUEBEC_HOLIDAY_YEARS_BEFORE;
    y <= year + QUEBEC_HOLIDAY_YEARS_AFTER;
    y++
  )
    out.push(...quebecHolidays(y));
  return out;
}
